import React from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  Award,
  LayoutDashboard,
  BookOpen,
  Trophy,
  Users,
  BarChart3,
  LogOut,
  GraduationCap,
  Sparkles,
  Megaphone,
} from "lucide-react";
import { useAuth } from "../lib/auth";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const linkBase =
    "flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors";
  const linkClasses = ({ isActive }) =>
    `${linkBase} ${
      isActive
        ? "bg-orange-600 text-white"
        : "text-slate-700 hover:bg-slate-100"
    }`;

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white flex" data-testid="app-layout">
      <aside className="w-64 shrink-0 bg-slate-50 border-r border-slate-200 min-h-screen sticky top-0 hidden md:flex md:flex-col">
        <Link to="/" className="flex items-center gap-2 px-5 py-5 border-b border-slate-200">
          <div className="w-8 h-8 bg-orange-600 grid place-items-center rounded-sm">
            <Award className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black tracking-tight">
            PLATINUM<span className="text-orange-600">1</span>
          </span>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          <NavLink to="/dashboard" data-testid="sidenav-dashboard" className={linkClasses}>
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </NavLink>
          <NavLink to="/courses" data-testid="sidenav-courses" className={linkClasses}>
            <BookOpen className="w-4 h-4" /> Courses
          </NavLink>
          <NavLink to="/certificate" data-testid="sidenav-certificate" className={linkClasses}>
            <Trophy className="w-4 h-4" /> Certificate
          </NavLink>
          {(!user.plan || user.plan === "free") && (
            <NavLink to="/pricing" data-testid="sidenav-pricing" className={linkClasses}>
              <Sparkles className="w-4 h-4" /> Upgrade
            </NavLink>
          )}
          {(user.role === "instructor" || user.role === "admin") && (
            <NavLink to="/instructor" data-testid="sidenav-instructor" className={linkClasses}>
              <GraduationCap className="w-4 h-4" /> Instructor
            </NavLink>
          )}
          {user.role === "admin" && (
            <NavLink to="/admin" end data-testid="sidenav-admin" className={linkClasses}>
              <Users className="w-4 h-4" /> Admin
            </NavLink>
          )}
          {user.role === "admin" && (
            <NavLink to="/admin/marketing" data-testid="sidenav-marketing" className={linkClasses}>
              <Megaphone className="w-4 h-4" /> Marketing
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-sm bg-orange-600 grid place-items-center text-white font-bold">
              {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{user.name || user.email}</div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1.5">
                {user.role}
                {user.plan && user.plan !== "free" && (
                  <span data-testid="user-plan-badge" className="bg-orange-600 text-white px-1.5 py-0.5 rounded-sm text-[10px] tracking-[0.15em]">
                    {user.plan.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-sm"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-orange-600 grid place-items-center rounded-sm">
            <Award className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-sm">PLATINUM<span className="text-orange-600">1</span></span>
        </Link>
        <button onClick={onLogout} data-testid="logout-btn-mobile" className="text-sm font-semibold text-slate-700">Sign out</button>
      </div>

      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
